import { useCallback, useEffect, useRef } from 'react';
import { SlotType, type CalDateInfo, type SlotInfo } from '../../../types/schedule.types';
import type { SceneInfo } from '../../../types/scene.types';
import type { BatchSlotLocationVisibilityItem } from '../../../api/schedule';
import { computeSlotLocationVisibilityDedupePatchesForDay } from '../../../utils/calDateSlotsSortUtils';

export interface UseSlotLocationVisibilityNormalizerParams {
  scheduleID: string | undefined;
  showAllLocations: boolean;
  optimisticSlots: SlotInfo[];
  optimisticCalDates: CalDateInfo[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  batchPatchSlotLocationVisibilityData: (
    scheduleID: string,
    items: BatchSlotLocationVisibilityItem[],
  ) => Promise<void>;
}

export function useSlotLocationVisibilityNormalizer({
  scheduleID,
  showAllLocations,
  optimisticSlots,
  optimisticCalDates,
  getSceneInfo,
  batchPatchSlotLocationVisibilityData,
}: UseSlotLocationVisibilityNormalizerParams) {
  const optimisticSlotsRef = useRef<SlotInfo[]>(optimisticSlots);
  optimisticSlotsRef.current = optimisticSlots;

  const optimisticCalDatesRef = useRef<CalDateInfo[]>(optimisticCalDates);
  optimisticCalDatesRef.current = optimisticCalDates;

  const queueRef = useRef<Set<string>>(new Set());
  const flushPromiseRef = useRef<Promise<void> | null>(null);
  const prevSortSigByDateRef = useRef<Map<string, string>>(new Map());

  const flush = useCallback(async () => {
    if (showAllLocations) return;
    if (!scheduleID) return;

    // A caller that enqueues while a batch is in flight must wait for that same
    // drain cycle. Previously it returned immediately, leaving the newly queued
    // date untouched until an unrelated render happened to call flush again.
    if (flushPromiseRef.current) {
      await flushPromiseRef.current;
      return;
    }
    if (queueRef.current.size === 0) return;
    const hasUnresolvedScene = optimisticSlotsRef.current.some(
      (slot) =>
        (slot.slotType === SlotType.SHOOT || !slot.slotType) &&
        !!slot.slotBelongSceneID &&
        !getSceneInfo(slot.slotBelongSceneID),
    );
    if (hasUnresolvedScene) return;

    const drainPromise = (async () => {
      while (queueRef.current.size > 0) {
        const pending = Array.from(queueRef.current);
        queueRef.current.clear();

        const slotsSnap = optimisticSlotsRef.current;
        const datesSnap = optimisticCalDatesRef.current;
        const items: BatchSlotLocationVisibilityItem[] = [];
        const missingDates: string[] = [];

        for (let i = 0; i < pending.length; i++) {
          const dateStr = pending[i];
          const pd = datesSnap.find((d) => d.calDateDt?.split('T')[0] === dateStr);
          if (!pd) {
            missingDates.push(dateStr);
            continue;
          }
          const patches = computeSlotLocationVisibilityDedupePatchesForDay(
            scheduleID,
            dateStr,
            pd,
            slotsSnap,
            (sceneId) => (getSceneInfo(sceneId)?.sceneLocation ?? '').trim(),
          );
          if (patches.length > 0) items.push(...patches);
        }

        // A temporary calDate may not have reached the optimistic cache yet.
        // Keep it queued for the state-change effect, but do not spin here.
        for (const dateStr of missingDates) queueRef.current.add(dateStr);

        if (items.length > 0) {
          await batchPatchSlotLocationVisibilityData(scheduleID, items);
        }

        const onlyUnresolvedDatesRemain =
          missingDates.length > 0 &&
          queueRef.current.size === missingDates.length &&
          missingDates.every((dateStr) => queueRef.current.has(dateStr));
        if (onlyUnresolvedDatesRemain) break;
      }
    })();

    flushPromiseRef.current = drainPromise;
    try {
      await drainPromise;
    } finally {
      if (flushPromiseRef.current === drainPromise) {
        flushPromiseRef.current = null;
      }
    }
  }, [showAllLocations, scheduleID, getSceneInfo, batchPatchSlotLocationVisibilityData]);

  const enqueue = useCallback((dateStrs: string[]) => {
    for (let i = 0; i < dateStrs.length; i++) {
      const ds = String(dateStrs[i] ?? '').trim();
      if (ds) queueRef.current.add(ds);
    }
  }, []);

  const enqueueAndFlush = useCallback(
    async (dateStrs: string[]) => {
      if (showAllLocations) return;
      enqueue(dateStrs);
      await flush();
    },
    [showAllLocations, enqueue, flush],
  );

  useEffect(() => {
    const nextMap = new Map<string, string>();
    const changedDates: string[] = [];

    for (let i = 0; i < optimisticCalDates.length; i++) {
      const pd = optimisticCalDates[i];
      if (!pd.calDateDt) continue;
      const dateStr = pd.calDateDt.split('T')[0];
      const sort = Array.isArray(pd.calDateSlotsSort) ? pd.calDateSlotsSort : [];
      const sig = `${sort.length}:${sort.join(',')}`;
      nextMap.set(dateStr, sig);

      const prevSig = prevSortSigByDateRef.current.get(dateStr);
      if (prevSig !== undefined && prevSig !== sig) {
        changedDates.push(dateStr);
      }
      if (prevSig === undefined) {
        changedDates.push(dateStr);
      }
    }

    prevSortSigByDateRef.current = nextMap;

    if (changedDates.length > 0) {
      enqueue(changedDates);
      flush();
    }
  }, [optimisticCalDates, enqueue, flush]);

  useEffect(() => {
    flush();
  }, [optimisticSlots, optimisticCalDates, flush]);

  return {
    enqueue,
    flush,
    enqueueAndFlush,
    inFlight: flushPromiseRef.current !== null,
  };
}
